/**
 * Minimal, dependency-free CSV serialization (#1288).
 *
 * Two hazards every cell must survive:
 *
 *   1. CSV / formula injection. A field whose text starts with `=` `+` `-` or
 *      `@` is executed as a live formula by Excel / Google Sheets /
 *      LibreOffice when the file is opened — a hostile record can exfiltrate
 *      data or run a command (an M4 review caught this exact class as a P1).
 *      We neutralize it by prefixing a single quote so the spreadsheet treats
 *      the value as literal text.
 *   2. RFC 4180 structure. A value containing a double quote, comma, CR or LF
 *      must be wrapped in double quotes with inner quotes doubled, or the row
 *      layout breaks.
 *
 * The two guards compose: the injection prefix is applied first, then RFC
 * quoting wraps the (possibly re-prefixed) value if it contains a special
 * character.
 *
 * We deliberately do NOT pull in a CSV library — the surface is tiny, the
 * escaping rules are fixed, and the injection guard must be auditable.
 */

// Characters that, when leading, make a spreadsheet interpret the cell as a
// formula. Besides the four formula sigils (`=` `+` `-` `@`), every leading
// whitespace glyph is included — space, tab (\t), CR (\r) and LF (\n) — per
// OWASP guidance: importers strip surrounding whitespace before evaluating the
// first real glyph, so ` =1+1` re-exposes the `=` unless we neutralize it here.
const INJECTION_PREFIX = /^[=+\-@\t\r\n ]/;

// Fields needing RFC 4180 quoting: contain a quote, comma, CR or LF.
const NEEDS_QUOTING = /["\n\r,]/;

/**
 * Coerce an arbitrary cell value to a CSV-safe field: neutralize formula
 * injection, then apply RFC 4180 quoting when required.
 */
export function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = typeof value === 'string' ? value : String(value);

  if (INJECTION_PREFIX.test(text)) {
    text = `'${text}`;
  }

  if (NEEDS_QUOTING.test(text)) {
    text = `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

/**
 * Serialize a matrix of already-computed cell values into an RFC 4180 CSV
 * string. Rows are joined with CRLF (the spec's canonical record separator);
 * each cell is escaped via {@link escapeCsvCell}.
 */
export function rowsToCsv(rows: unknown[][]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}

/**
 * Trigger a client-side download of `csv` as `filename`. A UTF-8 BOM is
 * prepended so Excel opens non-ASCII content (merchant names, zh labels) in
 * the correct encoding instead of mojibake.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
